import { type TestId } from '@repo/test-id';
import { type ReactNode } from 'react';
import { XIcon } from '@repo/icons';
import { Button } from '@repo/ui';

import PageHeader from '@shared/components/page/PageHeader';
import PageBottomButton from '@shared/components/page/PageBottomButton';

interface ForwardButtonProps {
  label: string;
  disabled?: boolean;
  pending?: boolean;
  topNote?: ReactNode;
  bottomNote?: ReactNode;
  onClick: () => void;
}

interface Props {
  children: ReactNode;
  testId: TestId;
  fullScreen?: boolean;
  headerText?: string;
  forwardButton?: ForwardButtonProps;
  drawer?: ReactNode;
  onGoBack?: () => void;
  onClose?: () => void;
}

export default function PageFrame({
  children,
  testId,
  fullScreen = false,
  headerText,
  forwardButton,
  drawer,
  onGoBack,
  onClose,
}: Props) {
  const handleGoBack = (): void => {
    onGoBack?.();
  };
  const handleClose = (): void => {
    onClose?.();
  };
  const handleGoForward = (): void => {
    forwardButton?.onClick();
  };

  return (
    <div className="relative flex min-h-full flex-col" data-testid={testId}>
      {(onGoBack || headerText) && (
        <PageHeader testId={testId.append('header')} headerText={headerText} onGoBack={handleGoBack} />
      )}

      {onClose && (
        <div className={fullScreen ? 'z-floating absolute top-5 left-5' : 'z-floating sticky top-5 pb-5 pl-5'}>
          <Button color="secondary" testId={testId.append('close')} iconOnly leftIcon={XIcon} onClick={handleClose} />
        </div>
      )}

      {fullScreen ? children : <div className="relative flex flex-1 flex-col gap-4 p-4">{children}</div>}

      {forwardButton && (
        <PageBottomButton
          label={forwardButton.label}
          testId={testId.append('next')}
          disabled={forwardButton.disabled}
          pending={forwardButton.pending}
          topNote={forwardButton.topNote}
          bottomNote={forwardButton.bottomNote}
          onClick={handleGoForward}
        />
      )}

      {drawer}
    </div>
  );
}
